import React, { useEffect, useState } from "react";
import "./staffCard.scss";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPhone } from "@fortawesome/pro-light-svg-icons";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { faClock, faEllipsisV } from "@fortawesome/pro-solid-svg-icons";
import { ButtonNew } from "components/buttonNew";
import { useHistory } from "react-router-dom";
import { Badge } from "components/badge/Badge";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { PostStaffAccountShiftClockIn, PutStaffAccountShiftClockOut } from "api/rpc/2024-04/facilityAdmin/client/shifts";
import { StatusCode } from "api/protocols";
import { setStaffAccountShift } from "redux/actions/auth";
import { IStaffAccountShift } from "redux/reducers/models/shift";
import moment from "moment";
import SelectNew from "components/select/SelectNew";

interface IStaffCardPerson {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  state?: string;
  phone_number?: string;
}

interface IProps {
  staff: IStaffCardPerson;
  staffRoles?: Array<{ id: number; title: string; wage?: number; }>
  onResetPassword: () => void;
  onDisableAccount: () => void;
  onReactivateAccount: () => void;
  editPath?: string;
  showClock?: boolean;
  // onDeleteAccount: () => void;
}

export function StaffCard(props: IProps) {
  const { staff, staffRoles, onResetPassword, onDisableAccount, onReactivateAccount, editPath, showClock = true } = props;
  const { authStore, facilityStore } = useAppSelector(store => store);
  const authedUser = authStore?.user?.permissions;
  const dispatch = useAppDispatch();
  const [clockTime, setClockTime] = useState<string>("");
  const [role, setRole] = useState<{ id: number; title: string; wage?: number; }>(null);

  const history = useHistory();
  const { t } = useTranslation();

  useEffect(() => {
    if (staff && staffRoles && staffRoles?.length > 0 && !role) {
      setRole(staffRoles[0]);
    }
  }, [staff]);

  useEffect(() => {
    function formatTime(timeDifference: moment.Duration) {
      const hours = timeDifference.hours().toString().padStart(2, "0");
      const minutes = timeDifference.minutes().toString().padStart(2, "0");
      const seconds = timeDifference.seconds().toString().padStart(2, "0");
      if (hours === "0" || hours === "00") {
        return `${minutes}:${seconds}`;
      } else {
        return `${hours}:${minutes}:${seconds}`;
      }
    }

    function updateClockTime() {
      const clockInTime = moment.utc((authStore?.shift?.clock_in_actual_at ?? authStore?.shift?.clock_in_at) as string)?.local();
      const currentTime = moment();
      const duration = moment.duration(currentTime.diff(clockInTime));
      const formattedElapsedTime = formatTime(duration);
      setClockTime(formattedElapsedTime);
    }

    let interval: number | null = null;

    if (authStore?.shift && authStore?.shift?.clock_in_at && !authStore?.shift?.clock_out_at) {
      //Update the clock time on tab open
      void updateClockTime();
      interval = window.setInterval(() => {
        //Update the clock time every second while the tab is open
        void updateClockTime();
      }, 1000);
    } else if (authStore?.shift && authStore?.shift?.clock_in_at && authStore?.shift?.clock_out_at) {
      setClockTime("00:00");
    }

    return () => {
      if (interval) {
        window.clearInterval(interval);
      }
    };
  }, [authStore?.shift]);

  const formatPhoneNumber = (phoneNumber: string): string => {
    const cleaned = phoneNumber?.replace(/\D/g, "");
    if (cleaned) {
      if (cleaned?.length === 10) {
        return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      }
    }
    return phoneNumber;
  };

  async function handleClockIn() {
    const clockInMeters = facilityStore?.teeSheetSettings?.clock_in_radius_meters;

    //Check location
    if (clockInMeters && clockInMeters > 0) {
      navigator.geolocation.getCurrentPosition(
        async position => {
          const { latitude, longitude } = position.coords;
          // Handle clock in with latitude, longitude
          const res = await PostStaffAccountShiftClockIn({ latitude, longitude, role_id: role?.id ? role?.id: undefined }, true);
          if (res?.status !== StatusCode.OK) {
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error clocking in")));
            return;
          }
          dispatch(showSuccess(t("Successfully clocked in")));
          dispatch(setStaffAccountShift(res?.data as IStaffAccountShift));
        },
        error => {
          // Location denied
          dispatch(showError(error.message));
        },
        {
          enableHighAccuracy: true,
        },
      );
    } else {
      //Clock in without checking location
      const res = await PostStaffAccountShiftClockIn({role_id: role?.id ? role?.id : undefined}, true);
      if (res?.status !== StatusCode.OK) {
        dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error clocking in")));
        return;
      }
      dispatch(showSuccess(t("Successfully clocked in")));
      dispatch(setStaffAccountShift(res?.data as IStaffAccountShift));
    }
  }

  async function handleClockOut() {
    const res = await PutStaffAccountShiftClockOut(null, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error clocking out")));
      return;
    }
    dispatch(showSuccess(t("Successfully clocked out")));
    dispatch(setStaffAccountShift(res?.data as IStaffAccountShift));
  }

  return (
    <div className="staff-card">
      <div className="staff-card-details">
        <div className="staff">
          <div className="flex">
            <div className="table-user-icon-container">
              <div className="table-user-icon-inner">
                {staff?.first_name?.charAt(0)}
                {staff?.last_name?.charAt(0)}
              </div>
            </div>
            <div className="pl-3">
              <div className="staff-name-and-badge mb-2">
                {/* <div> */}
                <div className="staff-name flex gap-1 items-center">
                  {staff?.first_name} {staff?.last_name}{" "}
                  {staff?.state === "disabled" ? (
                    <Badge type="warning">
                      <div>{t("Deactivated")}</div>
                    </Badge>
                  ) : staffRoles?.length > 0 ? (
                    staffRoles?.map((role, index) => (
                      <Badge key={index} type="lightblue" size="small">
                        <div className="text-blue-700">{role?.title}</div>
                      </Badge>))
                  ) : null}
                </div>
                {/* </div> */}
              </div>

              <div className="staff-email-and-phone">
                {staff?.email ? (
                  <>
                    <FontAwesomeIcon icon={faEnvelope} color="gray" className="pr-1" />
                    <div className="detail-text pr-2">{staff?.email}</div>
                  </>
                ) : null}
                {staff?.phone_number ? (
                  <>
                    | <FontAwesomeIcon icon={faPhone} color="gray" className="pl-2 pr-1" />
                    <div className="detail-text">{formatPhoneNumber(staff?.phone_number)}</div>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {/* Only the logged in user can clock in and out of their account */}
        {showClock && staff?.id === authStore?.user?.id ? (
          <div className="flex items-center gap-4">
            {staffRoles?.length > 1 && (!authStore?.shift?.clock_in_at || (authStore?.shift?.clock_in_at && authStore?.shift?.clock_out_at)) ? (
              <div style={{marginBottom: "1rem"}}>
              <SelectNew
                id={"role_selector"}
                label={t("Clock in as")}
                onChange={(_, role: { id: number; title: string; pivot: { user_id: number; role_id: number } }) => setRole(role)}
                value={role?.id}
                isLoading={!staffRoles}
                options={staffRoles?.map(role => ({ id: role?.id, label: role.title, extraValue: role }))}
              />
              </div>
            ) : null}

            {(!authStore?.shift || (authStore?.shift?.clock_in_at && authStore?.shift?.clock_out_at)) ? (
              <ButtonNew type="primary" size="small" block onClick={handleClockIn}><FontAwesomeIcon icon={faClock} className="mr-1" />{t("Clock In")}</ButtonNew>
            ) : (
              <ButtonNew type="primary" size="small" block onClick={handleClockOut} className="text-nowrap"><FontAwesomeIcon icon={faClock} className="mr-1" />{t("Clock Out")} ({clockTime})</ButtonNew>
            )}
          </div>
        ) : null}
        <div className="content-center">
          <NavigationDropdownNew
            showPlainTextLabel
            rightAlign
            label={
              <div>
                <ButtonNew style={{ padding: 10 }} size="medium" type="secondary">
                  <FontAwesomeIcon icon={faEllipsisV} />
                </ButtonNew>
              </div>
            }
            sections={[
              [
                {
                  label: t("Edit Staff Member"),
                  icon: "pen",
                  onClick: () => history.push(editPath ?? "/admin/settings/staff_edit_details/" + String(staff?.id)),
                  disabled: staff?.state === "disabled" || !authedUser?.admin_accounts_edit,
                },
                {
                  label: t("Reset Password"),
                  icon: "key",
                  onClick: () => onResetPassword(),
                  disabled: staff?.state === "disabled" || !authedUser?.admin_accounts_password_reset,
                },
                {
                  label: t("Disable Account"),
                  icon: "ban",
                  onClick: () => onDisableAccount(),
                  hidden: staff?.state === "disabled",
                  disabled: !authedUser?.admin_accounts_edit,
                },
                {
                  label: t("Reactivate Account"),
                  onClick: () => onReactivateAccount(),
                  icon: "power-off",
                  hidden: staff?.state !== "disabled",
                  disabled: !authedUser?.admin_accounts_edit,
                },
              ],
            ]}
          />
        </div>
      </div>
    </div>
  );
}
